import type {
  Order,
  OrderSource,
  OrderStatus,
  PaymentMethod,
  PickupMethod,
} from "@/mocks/branch/orders";

export interface StatusConfig {
  label: string;
  color: string;
  bg: string;
  icon: string;
}

export const ORDER_STATUS: Record<OrderStatus, StatusConfig> = {
  new: { label: "جديد", color: "#2E9E46", bg: "#E8F5E9", icon: "ri-sparkling-line" },
  preparing: { label: "جارٍ التحضير", color: "#B7791F", bg: "#FFF9E6", icon: "ri-fire-line" },
  almost_ready: { label: "قارب على الانتهاء", color: "#E8590C", bg: "#FFEDE0", icon: "ri-timer-flash-line" },
  ready: { label: "جاهز للاستلام", color: "#0D9488", bg: "#E6FFFA", icon: "ri-checkbox-circle-line" },
  out_for_delivery: { label: "خرج للتوصيل", color: "#B45309", bg: "#FDEFD8", icon: "ri-e-bike-2-line" },
  picked_up: { label: "مُستلَم", color: "#6B7280", bg: "#F3F4F6", icon: "ri-check-double-line" },
  not_picked: { label: "لم يُستلم", color: "#DC2626", bg: "#FEE2E2", icon: "ri-close-circle-line" },
};

/** هل الطلب «توصيل بواسطة المحل»؟ */
export function isDeliveryOrder(order: Order): boolean {
  return order.pickupMethod === "delivery";
}

/**
 * تسمية الحالة حسب طريقة الاستلام.
 * طلبات التوصيل فقط تستخدم: «جاهز للتوصيل» / «خرج للتوصيل» / «تم التسليم»،
 * وباقي الطرق (محلي/سفري/استلام من السيارة) تحافظ على تسمياتها الأصلية.
 */
export function statusLabelOf(order: Order, status: OrderStatus): string {
  if (isDeliveryOrder(order)) {
    if (status === "ready") return "جاهز للتوصيل";
    if (status === "out_for_delivery") return "خرج للتوصيل";
    if (status === "picked_up") return "تم التسليم";
  }
  return ORDER_STATUS[status].label;
}

export interface ActionConfig {
  label: string;
  color: string;
  textColor: string;
  confirmTitle: string;
  confirmMessage: string;
}

export const ACTION_CONFIG: Record<OrderStatus, ActionConfig | null> = {
  new: {
    label: "بدء التحضير",
    color: "#2E9E46",
    textColor: "#FFFFFF",
    confirmTitle: "بدء تحضير الطلب؟",
    confirmMessage: "سيتم تحويل حالة الطلب إلى «جارٍ التحضير» وبدء العدّاد.",
  },
  preparing: {
    label: "قارب على الانتهاء",
    color: "#B7791F",
    textColor: "#FFFFFF",
    confirmTitle: "تحديث الحالة إلى «قارب على الانتهاء»؟",
    confirmMessage: "سيتم إشعار العميل بأن الطلب على وشك الجاهزية.",
  },
  almost_ready: {
    label: "جاهز للاستلام",
    color: "#E8590C",
    textColor: "#FFFFFF",
    confirmTitle: "تحديث الحالة إلى «جاهز للاستلام»؟",
    confirmMessage: "سيظهر للعميل أن الطلب جاهز، ويبدأ عدّاد مهلة الاستلام.",
  },
  ready: {
    label: "تم الاستلام",
    color: "#0D9488",
    textColor: "#FFFFFF",
    confirmTitle: "تأكيد استلام الطلب؟",
    confirmMessage: "سيتم إغلاق الطلب كمُستلَم وإصدار الفاتورة إن كانت مستحقة.",
  },
  out_for_delivery: null,
  picked_up: null,
  not_picked: null,
};

/** إجراءات خاصة بطلبات التوصيل بواسطة المحل فقط */
const DELIVERY_ACTION_CONFIG: Partial<Record<OrderStatus, ActionConfig | null>> = {
  almost_ready: {
    label: "جاهز للتوصيل",
    color: "#0D9488",
    textColor: "#FFFFFF",
    confirmTitle: "تحديث الحالة إلى «جاهز للتوصيل»؟",
    confirmMessage: "سيصبح الطلب جاهزًا لبدء التوصيل بواسطة المحل.",
  },
  ready: {
    label: "خرج للتوصيل",
    color: "#B45309",
    textColor: "#FFFFFF",
    confirmTitle: "تأكيد خروج الطلب للتوصيل؟",
    confirmMessage: "سيُسجَّل أن الطلب خرج في طريقه إلى العميل.",
  },
  out_for_delivery: {
    label: "تم التسليم",
    color: "#6B7280",
    textColor: "#FFFFFF",
    confirmTitle: "تأكيد تسليم الطلب للعميل؟",
    confirmMessage: "سيتم إغلاق الطلب كـ«تم التسليم» وإصدار الفاتورة إن كانت مستحقة.",
  },
};

/** الإجراء التالي المناسب للطلب حسب حالته وطريقة الاستلام */
export function resolveAction(order: Order): ActionConfig | null {
  if (isDeliveryOrder(order)) {
    const deliveryAction = DELIVERY_ACTION_CONFIG[order.status];
    if (deliveryAction !== undefined) return deliveryAction;
  }
  return ACTION_CONFIG[order.status];
}

export const PICKUP_METHODS: Record<PickupMethod, { label: string; icon: string }> = {
  dine_in: { label: "محلي", icon: "ri-restaurant-line" },
  takeaway: { label: "سفري", icon: "ri-shopping-bag-line" },
  car: { label: "استلام من السيارة", icon: "ri-car-line" },
  delivery: { label: "توصيل بواسطة المحل", icon: "ri-e-bike-2-line" },
};

export const PAYMENT_METHODS: Record<PaymentMethod, { label: string; short: string; icon: string }> = {
  card: { label: "بطاقة", short: "بطاقة", icon: "ri-bank-card-line" },
  apple_pay: { label: "Apple Pay", short: "Apple Pay", icon: "ri-apple-line" },
  wallet: { label: "محفظة إلكترونية", short: "محفظة", icon: "ri-wallet-3-line" },
  cash: { label: "كاش", short: "كاش", icon: "ri-money-dollar-circle-line" },
  pos_terminal: {
    label: "إلكتروني — نقاط البيع",
    short: "نقاط البيع",
    icon: "ri-bank-card-2-line",
  },
};

export const SOURCE_LABELS: Record<OrderSource, { label: string; short: string; icon: string }> = {
  app: { label: "طلب عبر التطبيق", short: "تطبيق العميل", icon: "ri-smartphone-line" },
  pos: { label: "طلب من نقطة البيع POS", short: "نقطة البيع POS", icon: "ri-computer-line" },
};

export interface TabFilter {
  key: string;
  label: string;
  statuses: OrderStatus[] | null;
}

export const TAB_FILTERS: TabFilter[] = [
  { key: "all", label: "الكل", statuses: null },
  { key: "new", label: "جديد", statuses: ["new"] },
  { key: "preparing", label: "جارٍ التحضير", statuses: ["preparing"] },
  { key: "almost_ready", label: "قارب على الانتهاء", statuses: ["almost_ready"] },
  { key: "ready", label: "جاهز للاستلام", statuses: ["ready"] },
  { key: "completed", label: "مكتملة", statuses: ["picked_up", "not_picked"] },
];

export const DATE_FILTERS = [
  { key: "today", label: "اليوم" },
  { key: "week", label: "هذا الأسبوع" },
  { key: "month", label: "هذا الشهر" },
  { key: "custom", label: "نطاق مخصص" },
];

const FLOW: OrderStatus[] = ["new", "preparing", "almost_ready", "ready", "picked_up"];
const DELIVERY_FLOW: OrderStatus[] = [
  "new",
  "preparing",
  "almost_ready",
  "ready",
  "out_for_delivery",
  "picked_up",
];

export function nextStatusOf(order: Order): OrderStatus {
  const flow = isDeliveryOrder(order) ? DELIVERY_FLOW : FLOW;
  const idx = flow.indexOf(order.status);
  if (idx < 0 || idx >= flow.length - 1) return order.status;
  return flow[idx + 1];
}

export function firstStepLabel(order: Order): string {
  return order.source === "app" ? "تم استقبال الطلب من التطبيق" : "تم إنشاء الطلب";
}

const STEP_LABELS: Record<OrderStatus, string> = {
  new: "",
  preparing: "بدأ التحضير",
  almost_ready: "قارب على الانتهاء",
  ready: "جاهز للاستلام",
  out_for_delivery: "خرج للتوصيل",
  picked_up: "تم استلام الطلب",
  not_picked: "لم يُستلم — انتهت مهلة الاستلام",
};

function stepLabel(order: Order, status: OrderStatus): string {
  if (status === "new") return firstStepLabel(order);
  if (status === "not_picked" && isAppCashOrder(order)) return "تم تأكيد عدم الاستلام";
  if (isDeliveryOrder(order)) {
    if (status === "ready") return "جاهز للتوصيل";
    if (status === "out_for_delivery") return "خرج للتوصيل";
    if (status === "picked_up") return "تم التسليم";
  }
  return STEP_LABELS[status];
}

export interface TimelineEvent {
  status: OrderStatus;
  label: string;
  actorName: string;
  actorRole: string;
  time: string;
  isSystem: boolean;
  isCompleted: boolean;
  /** سبب الإغلاق — يظهر في مرحلة «لم يُستلم» فقط */
  note?: string;
}

/**
 * يبني مسار الحالة الكامل مع اسم منفذ الإجراء ودوره والوقت للمراحل المنفّذة.
 * المراحل التي لم تُنفّذ بعد تظهر «معلّقة» دون اسم موظف أو وقت تنفيذ.
 */
export function buildTimeline(
  order: Order,
  actorName: string,
  actorRole: string,
): TimelineEvent[] {
  const flow = isDeliveryOrder(order) ? DELIVERY_FLOW : FLOW;
  const endStatus: OrderStatus = order.status === "not_picked" ? "ready" : order.status;
  const endIndex = flow.indexOf(endStatus);
  const completedStatuses = endIndex >= 0 ? flow.slice(0, endIndex + 1) : [order.status];
  const completedIndex = new Map<OrderStatus, number>(
    completedStatuses.map((s, i) => [s, i]),
  );
  const n = completedStatuses.length;

  // الطلب «لم يُستلم» مغلق نهائيًا: لا تُعرض مرحلة الاستلام غير المنفذة داخل المسار،
  // ويكون آخر جزء في المسار هو «تم تأكيد عدم الاستلام» بواسطة موظف الفرع فقط.
  const rows: OrderStatus[] =
    order.status === "not_picked"
      ? [...completedStatuses, "not_picked"]
      : [...flow];

  return rows.map((s) => {
    const completed = s === "not_picked" || completedIndex.has(s);
    if (!completed) {
      return {
        status: s,
        label: stepLabel(order, s),
        actorName: "",
        actorRole: "",
        time: "",
        isSystem: false,
        isCompleted: false,
      };
    }
    const isSystem = s === "new" || (s === "not_picked" && !isAppCashOrder(order));
    const minutes =
      s === "not_picked"
        ? Math.max(0, order.minutesAgo)
        : Math.max(0, order.minutesAgo + (n - 1 - (completedIndex.get(s) ?? 0)) * 4);
    return {
      status: s,
      label: stepLabel(order, s),
      actorName: isSystem ? (s === "new" && order.source === "app" ? "التطبيق" : "النظام") : actorName,
      actorRole: isSystem ? "النظام" : actorRole,
      time: formatTimeAgo(minutes),
      isSystem,
      isCompleted: true,
      note:
        s === "not_picked"
          ? isAppCashOrder(order)
            ? "انتهت مهلة الاستلام والمهلة الإضافية، وأكّد موظف الفرع عدم استلام العميل للطلب."
            : "انتهت مهلة الاستلام والمهلة الإضافية دون أن يستلم العميل الطلب."
          : undefined,
    };
  });
}

/** هل الفاتورة متاحة الآن حسب المصدر ووسيلة الدفع؟ */
export function isInvoiceAvailable(order: Order): boolean {
  // لا تُعرض الفاتورة النهائية لأي طلب مسجّل على أنه «لم يُستلم»
  if (order.status === "not_picked") return false;
  // طلبات POS: تُصدر الفاتورة مباشرة بعد تأكيد الطلب
  if (order.source === "pos") return true;
  // طلبات التطبيق النقدية: تُصدر فقط عند التسليم وتحصيل المبلغ
  if (order.paymentMethod === "cash") return order.status === "picked_up" && Boolean(order.invoiceIssued);
  return Boolean(order.invoiceIssued);
}

/**
 * هل تُحتسب رسوم التطبيق على هذا الطلب؟
 * تُحتسب على طلبات التطبيق فقط، وتُلغى للطلبات المحلية (dine_in) داخل المطعم.
 */
export function hasAppFee(order: Order): boolean {
  return order.source === "app" && order.pickupMethod !== "dine_in" && order.appFee > 0;
}

/** هل تُسجَّل مديونية 0.80 ر.س على المحل عند بدء التحضير؟ (طلبات التطبيق النقدية غير المحلية فقط) */
export function shouldRecordDebt(order: Order): boolean {
  return hasAppFee(order) && order.paymentMethod === "cash";
}

/**
 * قيمة مديونية العميل عند «لم يُستلم» — محددة مسبقًا من إدارة التطبيق لقسم المتجر.
 * ثابتة: الموظف لا يحددها ولا يعدّلها ولا يُسقطها، ويؤكد الحالة فقط.
 */
export const CUSTOMER_DEBT_AMOUNT = 51;

/** هل الطلب «كاش منشأ من التطبيق»؟ */
export function isAppCashOrder(order: Order): boolean {
  return order.source === "app" && order.paymentMethod === "cash";
}

/** تفصيل ضريبة القيمة المضافة — الأسعار شاملة الضريبة */
export function vatBreakdown(total: number): { net: number; vat: number } {
  const vat = (total * 15) / 115;
  return { net: total - vat, vat };
}

export function formatMinutes(n: number): string {
  if (n === 1) return "دقيقة واحدة";
  if (n === 2) return "دقيقتين";
  if (n >= 3 && n <= 10) return `${n} دقائق`;
  return `${n} دقيقة`;
}

export function formatHours(n: number): string {
  if (n === 1) return "ساعة واحدة";
  if (n === 2) return "ساعتين";
  if (n >= 3 && n <= 10) return `${n} ساعات`;
  return `${n} ساعة`;
}

export function formatTimeAgo(minutesAgo: number): string {
  if (minutesAgo < 1) return "الآن";
  if (minutesAgo < 60) return `منذ ${formatMinutes(minutesAgo)}`;
  const hours = Math.floor(minutesAgo / 60);
  if (hours < 24) return `منذ ${formatHours(hours)}`;
  const days = Math.floor(hours / 24);
  return `منذ ${days} ${days === 1 ? "يوم" : "أيام"}`;
}

export function formatCountdown(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}